import { Component, inject, input } from "@angular/core";

import { CurrencyValuePipe, PercentageValuePipe } from "../../../../core/pipes";
import { AdaaHelper } from "../../../../core/utils";
import { Constants } from "../../../constants/constants";
import { LanguageService } from "../../../services";
import { CountUpComponent } from "../../count-up";

const LG_OUTER = 754;
const MD_OUTER = 628;
const SM_OUTER = 503;
const XS_OUTER = 314;
const XXS_OUTER = 188;

const LG_INNER = 584; // r=93
const MD_INNER = 484; // r=77
const SM_INNER = 390; // r=62
const XS_INNER = 239; // r=38
const XXS_INNER = 126; // r=20

@Component({
  selector: "adaa-dual-doughnut",
  templateUrl: "./dual-doughnut.html",
  styleUrl: "./dual-doughnut.scss",
  imports: [CountUpComponent, CurrencyValuePipe, PercentageValuePipe],
})
export class DualDoughnutComponent {
  readonly _languageService = inject(LanguageService);

  colorId1 = input.required<number>();
  colorId2 = input.required<number>();
  amount1 = input.required<number, number | null | undefined>({
    transform: ($a: number | null | undefined) => {
      if (!AdaaHelper.isDefined($a)) return 0;
      const $n = Number($a);
      return isNaN($n) ? 0 : $n;
    },
  });
  amount2 = input.required<number, number | null | undefined>({
    transform: ($a: number | null | undefined) => {
      if (!AdaaHelper.isDefined($a)) return 0;
      const $n = Number($a);
      return isNaN($n) ? 0 : $n;
    },
  });
  label1 = input<string>();
  label2 = input<string>();
  size = input<"lg" | "md" | "sm" | "xs" | "xxs">("sm");
  circleColor = input<string>("#d2d2d2");
  enableAnimation = input<boolean>(true);
  symbol = input<"%" | "N" | "C">("%");

  public get color1() {
    return Constants.COLOR_CODES[this.colorId1()];
  }

  public get color2() {
    return Constants.COLOR_CODES[this.colorId2()];
  }

  public get outerCurve() {
    const value = this.amount1();
    if (!value) return "0 999";
    return `${(this._outerFullCircle * value) / 100} 999`;
  }

  public get innerCurve() {
    const value = this.amount2();
    if (!value) return "0 999";
    return `${(this._innerFullCircle * value) / 100} 999`;
  }

  public get chartConfig() {
    return this._chartSize()[this.size()];
  }

  private get _outerFullCircle() {
    const map = { lg: LG_OUTER, md: MD_OUTER, sm: SM_OUTER, xs: XS_OUTER, xxs: XXS_OUTER };
    return map[this.size()];
  }

  private get _innerFullCircle() {
    const map = { lg: LG_INNER, md: MD_INNER, sm: SM_INNER, xs: XS_INNER, xxs: XXS_INNER };
    return map[this.size()];
  }

  private _chartSize() {
    return {
      lg: {
        cy: 140,
        cx: 140,
        outerR: 120,
        innerR: 93,
        container: "275px",
        label: { position: "5rem", amountFontSize: "1.6rem", labelFontSize: "0.85rem" },
      },
      md: {
        cy: 120,
        cx: 120,
        outerR: 100,
        innerR: 77,
        container: "235px",
        label: { position: "4.2rem", amountFontSize: "1.4rem", labelFontSize: "0.75rem" },
      },
      sm: {
        cy: 100,
        cx: 100,
        outerR: 80,
        innerR: 62,
        container: "195px",
        label: { position: "3.5rem", amountFontSize: "1.2rem", labelFontSize: "0.65rem" },
      },
      xs: {
        cy: 70,
        cx: 70,
        outerR: 50,
        innerR: 38,
        container: "135px",
        label: { position: "2.4rem", amountFontSize: "0.9rem", labelFontSize: undefined },
      },
      xxs: {
        cy: 50,
        cx: 50,
        outerR: 30,
        innerR: 20,
        container: "100px",
        label: { position: "1.7rem", amountFontSize: "0.65rem", labelFontSize: undefined },
      },
    };
  }
}
